import { cva } from 'class-variance-authority';

import { Button, type ButtonProps } from '@/components/buttons';
import { cn } from '@/lib/utils';

const INPUT_GROUP_BASE_SIZES = ['small', 'medium', 'large'] as const;

type InputGroupBaseSize = (typeof INPUT_GROUP_BASE_SIZES)[number];
type InputGroupButtonSize = NonNullable<ButtonProps['size']>;

const isInputGroupBaseSize = (size: InputGroupButtonSize): size is InputGroupBaseSize =>
  INPUT_GROUP_BASE_SIZES.includes(size as InputGroupBaseSize);

const sizeVariants = cva('', {
  variants: {
    size: {
      small: "h-6.5 gap-1 px-2 [&>svg:not([class*='size-'])]:size-3.5",
      medium: "h-7 gap-1.5 px-2 [&>svg:not([class*='size-'])]:size-4",
      large: "h-8 gap-1.5 px-2 [&>svg:not([class*='size-'])]:size-5",
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

export type InputGroupButtonProps = Omit<ButtonProps, 'icon'> & {
  size?: InputGroupButtonSize;
};

const InputGroupButton = ({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'medium',
  shape = 'square',
  children,
  ...props
}: InputGroupButtonProps) => {
  return (
    <Button
      type={type}
      variant={variant}
      size={size}
      shape={shape}
      className={cn(
        'flex items-center gap-2 text-sm shadow-none',
        'group-has-[[data-slot=input-group-control]:disabled]/input-group:pointer-events-none',
        'group-has-[[data-slot=input-group-control]:disabled]/input-group:[&_svg]:text-gray-500',
        isInputGroupBaseSize(size) ? sizeVariants({ size }) : 'p-0',
        className,
      )}
      {...props}
    >
      {children}
    </Button>
  );
};

export default InputGroupButton;
